import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Suspense, lazy } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Switch, Route, Link, Redirect, useRouteMatch } from 'react-router-dom';

import { ActionPopup, Loader } from '@components';

import {
  Calls as CallsIcon,
  Tickets as TicketIcon,
  Tasks as TasksIcon,
} from '@icons/navigation';
import {
  Avatar as ClientsIcon,
  Luggage as OrdersIcon,
  // ManagerEdit as TeamIcon,
} from '@icons/common';
import { Chart as StatisticIcon } from '@icons/crm';

import { ROUTE, PATH } from '../consts/pages';
import {
  AGENT_CLIENTS_PATH,
  AGENT_CLIENTS_ROUTE,
  AGENT_ORDERS_PATH,
  AGENT_ORDERS_ROUTE,
  AGENT_LEADS_PATH,
  AGENT_LEADS_ROUTE,
  B2BLeadsPage,
  // AGENT_TEAM_PATH,
  // AGENT_TEAM_ROUTE,
  B2BClientsPage,
  B2BOrdersPage,
  // B2BTeamPage,
} from '../modules/b2b';
import { resetServerErrors } from '../store/actions/general';
import HeadMeta from '../components/CRM/Common/HeadMeta';

const CallsPage = lazy(() => import('./pages/CallsPage'));
const TicketsPage = lazy(() => import('./pages/TicketsPage'));
const EmailPage = lazy(() => import('./pages/EmailPage'));
const StatisticPage = lazy(() => import('./pages/StatisticPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));

const mapDispatchToProps = (dispatch) => ({
  resetErrors: () => dispatch(resetServerErrors()),
});

const mapStateToProps = ({ appMode, general }) => {
  const error = get(general, 'serverError');

  return { error, isAgentMode: appMode.isAgentMode };
};

const NavLink = React.memo(({ children, path, to, ...props }) => {
  const match = useRouteMatch({
    path,
    exact: true,
  });

  return (
    <StyledLink to={to} active={match && match.isExact} {...props}>
      {children}
    </StyledLink>
  );
});

const Pages = React.memo(
  ({ error, isAgentMode, type, resetErrors, ...props }) => (
    <StyledPages {...props}>
      {!isAgentMode && <HeadMeta />}
      <StyledNavigation>
        {isAgentMode ? (
          <>
            <NavLink
              path={AGENT_LEADS_PATH}
              to={AGENT_LEADS_ROUTE}
              title="Заявки"
            >
              <TicketIcon />
            </NavLink>
            <NavLink
              path={AGENT_ORDERS_PATH}
              to={AGENT_ORDERS_ROUTE}
              title="Заказы"
            >
              <OrdersIcon />
            </NavLink>
            <NavLink
              path={AGENT_CLIENTS_PATH}
              to={AGENT_CLIENTS_ROUTE}
              title="Клиенты"
            >
              <ClientsIcon />
            </NavLink>
            {/* Страница турагентов временно исключена из MVP 1. */}
            {/* <NavLink
              path={AGENT_TEAM_PATH}
              to={AGENT_TEAM_ROUTE}
              title="Команда"
            >
              <TeamIcon />
            </NavLink> */}
          </>
        ) : (
          <>
            <NavLink path={PATH.CALLS} to={ROUTE.CALLS}>
              <CallsIcon />
            </NavLink>
            <NavLink path={PATH.TICKETS} to={ROUTE.TICKETS}>
              <TicketIcon />
            </NavLink>
            <NavLink path={PATH.STATSTIC} to={ROUTE.STATSTIC}>
              <StatisticIcon />
            </NavLink>
            <NavLink path={PATH.TASKS} to={ROUTE.TASKS}>
              <TasksIcon />
            </NavLink>
          </>
        )}
      </StyledNavigation>
      <StyledContent>
        <Suspense fallback={<Loader />}>
          <Switch>
            {isAgentMode && (
              <Redirect exact from={ROUTE.MAIN} to={AGENT_ORDERS_ROUTE} />
            )}
            {isAgentMode && (
              <Route path={AGENT_ORDERS_PATH}>
                <B2BOrdersPage />
              </Route>
            )}
            {isAgentMode && (
              <Route path={AGENT_LEADS_PATH}>
                <B2BLeadsPage />
              </Route>
            )}
            {isAgentMode && (
              <Route path={AGENT_CLIENTS_PATH}>
                <B2BClientsPage />
              </Route>
            )}
            {/* Страница турагентов временно исключена из MVP 1. */}
            {/* {isAgentMode && (
              <Route path={AGENT_TEAM_PATH}>
                <B2BTeamPage />
              </Route>
            )} */}
            {!isAgentMode && (
              <Route index exact path={ROUTE.MAIN}>
                <CallsPage />
              </Route>
            )}
            {!isAgentMode && (
              <Route path={PATH.CALLS}>
                <CallsPage />
              </Route>
            )}
            {!isAgentMode && (
              <Route path={PATH.TICKETS}>
                <TicketsPage />
              </Route>
            )}
            {!isAgentMode && (
              <Route path={PATH.MANAGER_EMAILS}>
                <EmailPage />
              </Route>
            )}
            {!isAgentMode && (
              <Route path={PATH.STATSTIC}>
                <StatisticPage />
              </Route>
            )}
            {!isAgentMode && (
              <Route path={PATH.TASKS}>
                <TasksPage />
              </Route>
            )}
            <Redirect to={isAgentMode ? AGENT_ORDERS_ROUTE : ROUTE.MAIN} />
          </Switch>
        </Suspense>
      </StyledContent>
      {error && (
        <ActionPopup
          title={error.title}
          description={error.description}
          isOpen
          onCancel={resetErrors}
          onSubmit={resetErrors}
        />
      )}
    </StyledPages>
  )
);

Pages.propTypes = {
  className: PropTypes.string,
  isAgentMode: PropTypes.bool.isRequired,
  setType: PropTypes.func.isRequired,
  resetErrors: PropTypes.func.isRequired,
  error: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  type: PropTypes.oneOf(Object.values(ROUTE)),
};

Pages.defaultProps = {
  className: '',
  error: null,
  type: ROUTE.CALLS,
};

NavLink.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
};

const StyledPages = styled.div`
  display: flex;
`;

const StyledNavigation = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  padding: 10px 8px;
  width: auto;

  @media print {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  border: none;
  border-radius: 50%;
  color: #4d4d4d;
  height: 52px;
  margin-right: 0;
  width: 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    height: 28px;
    width: 28px;
  }

  > svg,
  > svg * {
    fill: none;
    stroke: currentColor;
    stroke-width: 1;
    vector-effect: non-scaling-stroke;
  }

  > button {
    height: 100%;
    width: 100%;

    > * {
      border-radius: 0;
    }
  }

  ${({ active }) =>
    active &&
    `
     background: white;
  `}
`;

const StyledContent = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;
  overflow: auto;
`;

export default connect(mapStateToProps, mapDispatchToProps)(Pages);
